import { AppBar, Toolbar, Grid, Typography, IconButton } from '@material-ui/core'
import AccountCircleOutlineIcon from 'mdi-react/AccountCircleOutlineIcon'
import AccountGroupOutlineIcon from 'mdi-react/AccountGroupOutlineIcon'
import CalendarMonthOutlineIcon from 'mdi-react/CalendarMonthOutlineIcon'
import HomeCityOutlineIcon from 'mdi-react/HomeCityOutlineIcon'
import MessageOutlineIcon from 'mdi-react/MessageOutlineIcon'
import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { NavLanguageForm, DesktopMenu, BottomNav } from '.'
import { ReactComponent as MenuBarIcon } from '../../../images/navigation/MenuBarIcon.svg'
import { MuiClasses } from '../../../types'
import { isNumStep } from '../../../utils/apartments'
import { TalkUser, initialize } from '../../../utils/talkChat'
import { BaseNavMenuProps, NavMenuActionProps } from '../types'
//import NavBarPhoneNumber from './NavBarPhoneNumber'
//import LeeaLogo from '../../../images/LeeaLogo.svg'
import phoneIcon from '../../../images/phoneIcon.png'
import { useTranslation } from 'react-i18next'
import { matchPath } from 'react-router'

const navItems = [
  {
    id: '/overview',
    label: 'navigation.apartments',
    icon: HomeCityOutlineIcon
  },
  {
    id: '/applications',
    label: 'navigation.applicants',
    icon: AccountGroupOutlineIcon
  },
  {
    id: '/messages',
    label: 'navigation.messages',
    icon: MessageOutlineIcon
  },
  {
    id: '/showings/overview',
    label: 'navigation.calendar',
    icon: CalendarMonthOutlineIcon
  },
  {
    id: '/apartments',
    label: 'navigation.menu',
    icon: MenuBarIcon
  },
  {
    id: '/profile',
    label: 'navigation.profile',
    icon: AccountCircleOutlineIcon
  }
]

const adminUserNavItems = [
  {
    id: '/overview',
    label: 'navigation.apartments',
    icon: HomeCityOutlineIcon
  },
  {
    id: '/applications',
    label: 'navigation.applicants',
    icon: AccountGroupOutlineIcon
  },
  {
    id: '/messages',
    label: 'navigation.messages',
    icon: MessageOutlineIcon
  },
  {
    id: '/showings/overview',
    label: 'navigation.calendar',
    icon: CalendarMonthOutlineIcon
  },
  {
    id: '/apartments',
    label: 'navigation.menu',
    icon: MenuBarIcon
  },
  {
    id: '/profile',
    label: 'navigation.profile',
    icon: AccountCircleOutlineIcon
  }
]

interface NavBarProps {
  isAuthenticated: boolean
  isAdminUser: boolean
  classes: MuiClasses
  pathname: string
  doNavigation: (path: string) => void
  showLanguageSwitcher: boolean
  userAttributes: any
}

const NavBar = ({
  isAuthenticated,
  isAdminUser,
  classes,
  doNavigation,
  showLanguageSwitcher,
  pathname,
  userAttributes
}: NavBarProps) => {
  const { t } = useTranslation()
  const smallScreen = window.innerWidth < 1066;
  const verySmallScreen = window.innerWidth < 330;
  const currentPathEnd = pathname.includes('/showings/overview')
    ? '/showings/overview'
    : pathname.substring(pathname.lastIndexOf('/'))
  const baseNavMenuProps: BaseNavMenuProps = { classes }
  const isNewApplication = (pathname: string) =>
    pathname.split('/')[1] == 'new-application'
  const [unreadMessages, setUnreadMessages] = useState(0)
  const safeUnreadMessages = Number.isFinite(unreadMessages)
    ? unreadMessages
    : 0
  const navMenuActionProps: NavMenuActionProps = {
    doNavigation,
    navItems: isAdminUser ? adminUserNavItems : navItems,
    currentPathEnd,
    unreadMessages: safeUnreadMessages
  }
  const unreadMessageEvent = () => {
    if (window.talkSession) {
      const initialUnread = Number.isFinite(window.talkSession.unread)
        ? Number(window.talkSession.unread)
        : 0
      setUnreadMessages(initialUnread)
      window.talkSession.unreads.on(
        'change',
        function (unreadConversations: any) {
          const delta = Array.isArray(unreadConversations)
            ? unreadConversations.length
            : Number(unreadConversations) || 0
          setUnreadMessages((prev: any) => {
            const safePrev = Number.isFinite(prev) ? Number(prev) : 0
            return safePrev + delta
          })
        }
      )
    }
  }

  const createChatSession = async () => {
    if (isAuthenticated && !isAdminUser && userAttributes?.email) {
      const usersFirstName = userAttributes.name.split(' ')[0]
      const talkUser: TalkUser = {
        id: userAttributes.email,
        name: usersFirstName,
        email: userAttributes.email,
        phone: userAttributes?.phone_number,
        role: isAdminUser ? 'admin' : 'owner'
      }
      await initialize(talkUser)
      unreadMessageEvent()
    }
  }

  useEffect(() => {
    ; (async () => {
      setImmediate(async () => {
        await createChatSession()
      })
    })()

    /* eslint-disable-next-line @typescript-eslint/no-empty-function */
    return () => { }
  }, [isAuthenticated, isAdminUser, userAttributes])

  const apartmentMatch = matchPath(pathname, {
    path: '/users/:userId/apartments/:apartmentId'
  })
  const isApartmentContext =
    Boolean(apartmentMatch?.params?.userId && apartmentMatch?.params?.apartmentId) &&
    !pathname.includes('/steps')
  const isApartmentStepRoute = /\/(create\/apartment|users\/[^/]+\/apartments\/[^/]+)\/steps\//.test(
    pathname
  )
  const showMenuIcon =
    isApartmentContext || pathname.startsWith('/profile') || isApartmentStepRoute
  const headerIconTarget = showMenuIcon ? '/apartments' : '/profile'

  return (
    <Fragment>
      <AppBar className={classes.appBar}>
        <Toolbar className={classes.navToolbar}>
          <Grid
            className={classes.menuItems}
            container
            direction='row'
            alignItems='center'
            justify='space-between'
            style={{
              paddingRight: isNewApplication(pathname)
                ? smallScreen
                  ? '0px'
                  : '50px'
                : '0px'
            }}
          >
            <Grid item>
              <Typography className={classes.logoContainer}>
                {pathname.includes('/reserve') ||
                pathname.includes('/create') ||
                isNumStep(pathname, '10') ? (
                  <Typography>
                    <img
                      className={classes.logo}
                      style={{ color: '#fff', width: '100%' }}
                      src='https://fiksuvuokraus.fi/wp-content/uploads/2023/03/Fiksuvuokraus.fi_logo.png'
                      alt='Fiksuvuokraus.fi Logo'
                    />
                  </Typography>
                ) : pathname.includes('/tenants') ? (
                  <a
                    href='https://fiksuvuokraus.fi'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <img
                      className={classes.logo}
                      style={{ color: '#fff', width: '100%' }}
                      src='https://fiksuvuokraus.fi/wp-content/uploads/2023/03/Fiksuvuokraus.fi_logo.png'
                      alt='Fiksuvuokraus.fi Logo'
                    />
                  </a>
                ) : (
                  <Link to='/'>
                    <img
                      className={classes.logo}
                      style={{ color: '#fff', width: '100%' }}
                      src='https://fiksuvuokraus.fi/wp-content/uploads/2023/03/Fiksuvuokraus.fi_logo.png'
                      alt='Fiksuvuokraus.fi Logo'
                    />
                  </Link>
                )}
                {!pathname.includes('/new-application') && !pathname.includes('/suggest') && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={phoneIcon}
                      style={{ width: '24px', height: '24px', marginRight: '2px', marginLeft: verySmallScreen ? '10px' : '20px', }}
                      alt="Phone Icon"
                    />
                    <a href="javascript:void(0);" onClick={() => window.location.href = 'tel:+358406706816'} style={{ textDecoration: 'none', color: 'inherit', fontSize: verySmallScreen ? '0.85rem' : 'inherit', }}>
                      0406706816
                    </a>
                  </div>
                )}
                {/* {!isNewApplication(pathname) && isAuthenticated ? (
                  <NavBarPhoneNumber
                    {...{ isAuthenticated, smallScreen, classes, isTinyScreen }}
                  />
                ) : null} */}
              </Typography>
            </Grid>
            {/* {!isNewApplication(pathname) && !isAuthenticated ? (
              <NavBarPhoneNumber
                {...{ isAuthenticated, smallScreen, classes, isTinyScreen }}
              />
            ) : null} */}
            {isAuthenticated ? (
              smallScreen ? null : (
                <DesktopMenu {...baseNavMenuProps} {...navMenuActionProps} />
              )
            ) : (
              showLanguageSwitcher && (
                <NavLanguageForm classes={classes} smallScreen={smallScreen} />
              )
            )}
          </Grid>
          {smallScreen && isAuthenticated && !(isApartmentContext || isApartmentStepRoute) && (
            <Grid item>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IconButton
                  color='inherit'
                  onClick={() => {
                    doNavigation(headerIconTarget)
                  }}
                >
                  {showMenuIcon ? (
                    <MenuBarIcon />
                  ) : (
                    <AccountCircleOutlineIcon />
                  )}
                </IconButton>
                <Typography variant='caption' style={{ color: '#fff', fontWeight: 600, marginTop: -6 }}>
                  {(showMenuIcon
                    ? t('navigation.menu')
                    : t('navigation.profile')
                  ).toUpperCase()}
                </Typography>
              </div>
            </Grid>
          )}
        </Toolbar>
      </AppBar>
      {smallScreen && isAuthenticated && (
        <BottomNav {...baseNavMenuProps} {...navMenuActionProps} />
      )}
    </Fragment>
  )
}

export default NavBar
